import { useTranslation } from "react-i18next";
import { useActiveTeam } from "@/hooks/useActiveTeam";
import { Users, AlertTriangle } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";

interface ActiveTeamSelectorProps {
  collapsed?: boolean;
}

/**
 * Team selector shown in sidebar for multi-team users.
 * Hidden when user belongs to 0 or 1 team.
 */
// TES-1664: pre-existing selector states; this PR only adds an accessible name/contrast tweak.
// fallow-ignore-next-line complexity
export function ActiveTeamSelector({ collapsed = false }: ActiveTeamSelectorProps) {
  const { t } = useTranslation();
  const {
    userTeams,
    activeTeam,
    activeTeamId,
    isMultiTeam,
    needsTeamSelection,
    setActiveTeam,
    isLoading,
    isSetting,
  } = useActiveTeam();

  // Don't render for single-team or no-team users
  if (!isMultiTeam || isLoading) return null;

  if (collapsed) {
    return (
      <div className="px-2 py-1" data-testid="active-team-selector">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center justify-center">
                <div className="relative">
                  {needsTeamSelection ? (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  ) : (
                    <Users className="h-5 w-5 text-sidebar-foreground/80" />
                  )}
                  <Badge
                    variant="secondary"
                    className={cn(
                      "absolute -top-2 -right-2 h-4 min-w-4 px-1 text-[10px] flex items-center justify-center",
                      needsTeamSelection
                        ? "bg-destructive text-destructive-foreground"
                        : "bg-primary text-primary-foreground",
                    )}
                  >
                    {userTeams.length}
                  </Badge>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right">
              {needsTeamSelection
                ? t("teams.pleaseSelectTeam", "Bitte Team wählen")
                : (activeTeam?.name ?? t("teams.selectTeam", "Team wählen"))}
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
    );
  }

  return (
    <div className="px-3 pt-2" data-testid="active-team-selector">
      <Select
        value={activeTeamId ?? ""}
        onValueChange={(value) => setActiveTeam(value || null)}
        disabled={isSetting}
      >
        <SelectTrigger
          aria-label={t("teams.selectTeam", "Team wählen")}
          className={cn(
            "w-full h-9 text-sm",
            needsTeamSelection
              ? "bg-destructive/10 border-destructive text-destructive font-semibold animate-pulse"
              : "bg-sidebar-accent/50 border-sidebar-border hover:bg-sidebar-accent",
            activeTeamId && !needsTeamSelection && "text-sidebar-foreground font-semibold",
          )}
        >
          <div className="flex items-center gap-2 truncate">
            {needsTeamSelection ? (
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            ) : (
              <Users className="h-4 w-4 flex-shrink-0" />
            )}
            <SelectValue
              placeholder={
                needsTeamSelection
                  ? t("teams.pleaseSelectTeam", "Bitte Team wählen")
                  : t("teams.selectTeam", "Team wählen")
              }
            />
          </div>
        </SelectTrigger>
        <SelectContent>
          {userTeams.map((team) => (
            <SelectItem key={team.id} value={team.id}>
              {team.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
